import { type ChangeEvent, type KeyboardEvent, useEffect, useState } from 'react';
import {
  AdornedInput,
  Box,
  FormField,
  FormHelp,
  Heading,
  Label,
  List,
  Paragraph,
  Popover,
  TextButton,
} from '@zillow/constellation-10';
import { YEARLY_INCOME_LIMIT } from './constants';
import { isValidNumberKeyDown } from '../../utils/isValidNumberKeyDown';

type IncomeInputFieldProps = {
  value: number | null;
  onChange: (annualIncome: number | null) => void;
  /** Portal target for the info popover. */
  portalRoot?: HTMLElement | null;
};

const formatCurrency = (num: number): string => num.toLocaleString('en-US');

const parseCurrency = (str: string): number | null => {
  const cleaned = str.replace(/,/g, '');
  const num = Number(cleaned);
  return isNaN(num) ? null : num;
};

export const IncomeInputField = ({ value, onChange, portalRoot }: IncomeInputFieldProps) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    setDisplayValue(value ? formatCurrency(value) : '');
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsed = parseCurrency(inputValue);

    if (parsed === null) {
      setDisplayValue('');
      setIsInvalid(false);
      return;
    }

    setDisplayValue(formatCurrency(parsed));
    setIsInvalid(parsed > YEARLY_INCOME_LIMIT);
  };

  const handleBlur = () => {
    const parsed = parseCurrency(displayValue);

    if (parsed === null || displayValue === '') {
      onChange(null);
      return;
    }

    if (!isInvalid) {
      onChange(parsed);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isValidNumberKeyDown(e.key)) {
      e.preventDefault();
    }
  };

  const errorMessage = `Yearly income cannot exceed $${formatCurrency(YEARLY_INCOME_LIMIT)}`;

  return (
    <FormField
      label={
        <Label data-testid="income-input-label">
          <Box css={{ display: 'inline-flex', alignItems: 'center', gap: 'tighter', flexDirection: 'row' }}>
            <Box>Estimated household income</Box>
            <Popover
              header={<Heading level={2}>How to estimate income</Heading>}
              portalRoot={portalRoot}
              body={
                <>
                  <Paragraph>
                    Estimate all sources of income paid to the adults in your household. This might include pre-tax
                    details from:
                  </Paragraph>
                  <List.Root css={{ paddingBlockStart: 'tighter' }}>
                    <List.Item>A W-2 or 1099 form</List.Item>
                    <List.Item>Social Security Disability Insurance</List.Item>
                    <List.Item>Rental assistance subsidies</List.Item>
                  </List.Root>
                  <Paragraph css={{ paddingBlockStart: 'tighter' }}>
                    In some areas, it&rsquo;s illegal to discriminate against or treat renters differently based on how
                    they pay rent.{' '}
                  </Paragraph>
                  <Box css={{ marginBlockStart: 'default' }}>
                    <TextButton asChild tone="brand" textStyle="body-bold">
                      <a
                        href="https://www.zillow.com/learn/renters-protected-source-of-income-discrimination/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </a>
                    </TextButton>
                  </Box>
                </>
              }
              dividers
              shouldAwaitInteractionResponse
              tone="info"
            />
          </Box>
        </Label>
      }
      control={
        <AdornedInput
          input={
            <AdornedInput.Input
              data-testid="income-input"
              type="text"
              inputMode="numeric"
              value={displayValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              error={isInvalid}
            />
          }
          startAdornment={<AdornedInput.Adornment data-testid="income-input-adornment">$</AdornedInput.Adornment>}
          endAdornment={
            <AdornedInput.Adornment data-testid="income-input-period-adornment">/year</AdornedInput.Adornment>
          }
          fluid
        />
      }
      description={
        isInvalid ? (
          <FormHelp data-testid="income-input-error" error>
            {errorMessage}
          </FormHelp>
        ) : undefined
      }
      error={isInvalid}
      optional
    />
  );
};
